import { useEffect, useState } from 'react';
import {
  Modal, Table, Button, Form, Input, Select, Space, Popconfirm, message, Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../api';

const TYPE_LABELS = { customer: 'Kunde', supplier: 'Lieferant', both: 'Beides' };
const TYPE_COLORS = { customer: 'blue', supplier: 'orange', both: 'green' };

export default function ContactsModal({ open, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setContacts(await api.contacts.list());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const openNew = () => {
    setEditingContact(null);
    form.resetFields();
    form.setFieldsValue({ type: 'both', country: 'Deutschland' });
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditingContact(record);
    form.setFieldsValue(record);
    setFormOpen(true);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      if (editingContact) {
        await api.contacts.update(editingContact.id, values);
        message.success('Kontakt gespeichert');
      } else {
        await api.contacts.create(values);
        message.success('Kontakt angelegt');
      }
      setFormOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return; // Ant Design Validierungsfehler – inline angezeigt
      message.error(e.message);
    }
  };

  const del = async (id) => {
    try {
      await api.contacts.delete(id);
      message.success('Kontakt gelöscht');
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    {
      title: 'Typ', dataIndex: 'type', key: 'type', width: 110,
      render: (t) => <Tag color={TYPE_COLORS[t]}>{TYPE_LABELS[t]}</Tag>,
    },
    { title: 'Ort', key: 'city', render: (_, r) => [r.zip, r.city].filter(Boolean).join(' ') },
    { title: 'E-Mail', dataIndex: 'email', key: 'email' },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
    {
      title: '', key: 'actions', width: 90,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="Wirklich löschen?" onConfirm={() => del(record.id)} okText="Ja" cancelText="Nein">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Kontakte verwalten"
        open={open}
        onCancel={onClose}
        footer={<Button onClick={onClose}>Schließen</Button>}
        width={900}
      >
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Neuer Kontakt</Button>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={contacts}
          loading={loading}
          size="small"
          pagination={false}
        />
      </Modal>

      <Modal
        title={editingContact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
        open={formOpen}
        onOk={save}
        onCancel={() => setFormOpen(false)}
        okText="Speichern"
        cancelText="Abbrechen"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="type" label="Typ" rules={[{ required: true }]}>
            <Select options={[
              { value: 'customer', label: 'Kunde' },
              { value: 'supplier', label: 'Lieferant' },
              { value: 'both', label: 'Kunde & Lieferant' },
            ]} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Pflichtfeld' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="street" label="Straße">
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item name="zip" label="PLZ" style={{ width: 120 }}>
              <Input />
            </Form.Item>
            <Form.Item name="city" label="Ort" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="country" label="Land">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="E-Mail">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="Telefon">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
